import { useState, useEffect, useRef } from "react";
import { Outlet, NavLink, useNavigate, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { 
  LayoutDashboard, 
  Users, 
  ShoppingCart, 
  Truck,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ChevronDown,
  Database,
  Tag,
  Package,
  Image,
  CreditCard,
  Settings,
  UserCheck,
  Highlighter,
  BellRing,
  Sun,
  Moon,
  Monitor,
  Gift
} from "lucide-react";
import { toast } from "sonner";
import { useTheme } from "next-themes";

const LOGO_URL = "https://customer-assets-lxgj4vgw.emergentagent.net/job_summary-hub-95/artifacts/v1xz43s3_Gasman%20Logo.png";
const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
const ORDER_EVENTS_URL = `${BACKEND_URL}/api/admin/orders/events`;

const sidebarItems = [
  { name: "Dashboard", path: "/admin/dashboard", icon: LayoutDashboard },
  { 
    name: "Masters", 
    icon: Database,
    children: [
      { name: "Categories", path: "/admin/masters/categories", icon: Tag },
      { name: "Products", path: "/admin/masters/products", icon: Package },
      { name: "Banners", path: "/admin/masters/banners", icon: Image },
      { name: "Payment Methods", path: "/admin/masters/payment-methods", icon: CreditCard },
      { name: "Highlight", path: "/admin/masters/highlight", icon: Highlighter },
    ]
  },
  { name: "Stores", path: "/admin/stores", icon: Truck },
  { name: "Orders", path: "/admin/orders", icon: ShoppingCart },
  { name: "Users", path: "/admin/users", icon: Users },
  { name: "Rewards", path: "/admin/rewards", icon: Gift },
  { name: "Delivery Managers", path: "/admin/delivery-managers", icon: UserCheck },
  { name: "Settings", path: "/admin/settings", icon: Settings },
];

const themeOptions = [
  { value: "light", label: "Light", icon: Sun },
  { value: "dark", label: "Dark", icon: Moon },
  { value: "system", label: "System", icon: Monitor },
];

const ThemeSelector = () => {
  const { theme, setTheme } = useTheme();

  return (
    <div className="mt-3 grid grid-cols-3 gap-1 rounded-lg border border-gray-200 bg-gray-50 p-1" aria-label="Color theme">
      {themeOptions.map(({ value, label, icon: Icon }) => {
        const selected = theme === value;
        return (
          <button
            key={value}
            type="button"
            onClick={() => setTheme(value)}
            className={`flex flex-col items-center justify-center gap-1 rounded-md px-1 py-2 text-[10px] font-semibold transition-colors ${
              selected
                ? "bg-white text-[#0A4CAE] shadow-sm"
                : "text-gray-500 hover:bg-white/70 hover:text-gray-800"
            }`}
            aria-pressed={selected}
            title={`${label} mode`}
          >
            <Icon size={15} />
            <span>{label}</span>
          </button>
        );
      })}
    </div>
  );
};

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mastersOpen, setMastersOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [newOrder, setNewOrder] = useState(null);
  const [newOrderCount, setNewOrderCount] = useState(0);
  const newOrderRef = useRef(null);
  const orderSoundRef = useRef(null);

  useEffect(() => {
    const storedUser = localStorage.getItem("gasman_user");
    if (storedUser) {
      setUser(JSON.parse(storedUser));
    }
    // Auto-expand Masters if on a masters page
    if (location.pathname.includes("/admin/masters")) {
      setMastersOpen(true);
    }
  }, [location.pathname]);

  useEffect(() => {
    const orderSound = new Audio("/audio/order-notification.mp3");
    orderSound.preload = "auto";
    orderSoundRef.current = orderSound;

    const unlockAudio = async () => {
      try {
        orderSound.muted = true;
        await orderSound.play();
        orderSound.pause();
        orderSound.currentTime = 0;
        orderSound.muted = false;
      } catch (_) {
        orderSound.muted = false;
      }
    };

    window.addEventListener("pointerdown", unlockAudio, { once: true });
    window.addEventListener("keydown", unlockAudio, { once: true });
    return () => {
      window.removeEventListener("pointerdown", unlockAudio);
      window.removeEventListener("keydown", unlockAudio);
      orderSound.pause();
      orderSoundRef.current = null;
    };
  }, []);

  useEffect(() => {
    let stopped = false;
    let abortController;
    let reconnectTimer;

    const playOrderSound = () => {
      const orderSound = orderSoundRef.current;
      if (!orderSound) return;
      orderSound.currentTime = 0;
      orderSound.play().catch(() => {
        // The modal remains the primary alert if browser audio is unavailable.
      });
    };

    const handleOrderMessage = (message) => {
      if (message.type !== "new_order" || !message.order) return;

      setNewOrderCount((count) => newOrderRef.current ? count + 1 : 1);
      newOrderRef.current = message.order;
      setNewOrder(message.order);
      playOrderSound();
    };

    const connect = async () => {
      const token = localStorage.getItem("gasman_token");
      if (!token || stopped) return;

      abortController = new AbortController();
      try {
        const response = await fetch(ORDER_EVENTS_URL, {
          headers: { Authorization: `Bearer ${token}` },
          signal: abortController.signal,
        });
        if (response.status === 401 || response.status === 403) {
          localStorage.removeItem("gasman_token");
          localStorage.removeItem("gasman_user");
          window.location.replace("/admin/login");
          return;
        }
        if (!response.ok || !response.body) {
          throw new Error(`Order event stream failed with status ${response.status}`);
        }

        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (!stopped) {
          const { value, done } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const events = buffer.split("\n\n");
          buffer = events.pop() || "";
          events.forEach((event) => {
            const dataLine = event.split("\n").find((line) => line.startsWith("data: "));
            if (!dataLine) return;
            try {
              handleOrderMessage(JSON.parse(dataLine.slice(6)));
            } catch (error) {
              console.error("Unable to read new order notification", error);
            }
          });
        }
      } catch (error) {
        if (!stopped && error.name !== "AbortError") {
          console.error("Order notification stream disconnected", error);
        }
      } finally {
        if (!stopped) reconnectTimer = window.setTimeout(connect, 3000);
      }
    };

    connect();
    return () => {
      stopped = true;
      window.clearTimeout(reconnectTimer);
      if (abortController) abortController.abort();
    };
  }, []);

  const closeNewOrderModal = () => {
    newOrderRef.current = null;
    setNewOrder(null);
    setNewOrderCount(0);
  };

  const handleLogout = () => {
    localStorage.removeItem("gasman_token");
    localStorage.removeItem("gasman_user");
    toast.success("Logged out successfully");
    navigate("/");
  };

  return (
    <div className="admin-shell flex h-screen overflow-hidden bg-[#f8fafc] dark:bg-slate-950">
      {/* Desktop Sidebar */}
      <aside 
        className={`relative hidden h-screen flex-shrink-0 flex-col admin-sidebar md:flex ${sidebarOpen ? 'w-64' : 'w-20'} transition-all duration-300`}
        data-testid="admin-sidebar"
      >
        {/* Logo */}
        <div className="p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="GasMan4U" className="h-10 w-10 object-contain" />
            {sidebarOpen && (
              <span className="text-lg font-bold text-gray-900">GasMan4U</span>
            )}
          </div>
          {sidebarOpen && <ThemeSelector />}
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute top-4 -right-3 w-6 h-6 bg-white border border-gray-200 rounded-full flex items-center justify-center text-gray-500 hover:text-gray-900 shadow-sm"
          data-testid="sidebar-toggle"
        >
          <ChevronRight size={14} className={`transition-transform ${sidebarOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Navigation */}
        <nav className="min-h-0 flex-1 space-y-1 overflow-y-auto p-4">
          {sidebarItems.map((item) => (
            item.children ? (
              <div key={item.name}>
                <button
                  onClick={() => setMastersOpen(!mastersOpen)}
                  className={`sidebar-item w-full justify-between ${location.pathname.includes("/admin/masters") ? 'text-[#0A4CAE]' : ''}`}
                  data-testid={`sidebar-${item.name.toLowerCase()}`}
                >
                  <div className="flex items-center gap-3">
                    <item.icon size={20} />
                    {sidebarOpen && <span>{item.name}</span>}
                  </div>
                  {sidebarOpen && (
                    <ChevronDown 
                      size={16} 
                      className={`transition-transform ${mastersOpen ? 'rotate-180' : ''}`} 
                    />
                  )}
                </button>
                {mastersOpen && sidebarOpen && (
                  <div className="ml-4 mt-1 space-y-1 border-l border-gray-200 pl-3">
                    {item.children.map((child) => (
                      <NavLink
                        key={child.path}
                        to={child.path}
                        className={({ isActive }) => 
                          `sidebar-item text-sm ${isActive ? 'active' : ''}`
                        }
                        data-testid={`sidebar-${child.name.toLowerCase()}`}
                      >
                        <child.icon size={18} />
                        <span>{child.name}</span>
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => 
                  `sidebar-item ${isActive ? 'active' : ''}`
                }
                data-testid={`sidebar-${item.name.toLowerCase()}`}
              >
                <item.icon size={20} />
                {sidebarOpen && <span>{item.name}</span>}
              </NavLink>
            )
          ))}
        </nav>

        {/* User & Logout */}
        <div className="p-4 border-t border-gray-200">
          {sidebarOpen && user && (
            <div className="mb-4 px-3">
              <p className="text-gray-900 font-medium truncate">{user.name}</p>
              <p className="text-gray-500 text-sm truncate">{user.email}</p>
            </div>
          )}
          <Button
            onClick={handleLogout}
            variant="ghost"
            className={`w-full justify-start gap-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100 ${!sidebarOpen && 'justify-center'}`}
            data-testid="logout-btn"
          >
            <LogOut size={20} />
            {sidebarOpen && <span>Logout</span>}
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-white border-b border-gray-200">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <img src={LOGO_URL} alt="GasMan4U" className="h-8 w-8 object-contain" />
            <span className="font-bold text-gray-900">GasMan4U</span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-gray-900"
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="max-h-[calc(100vh-4rem)] overflow-y-auto bg-white border-b border-gray-200 p-4">
            <ThemeSelector />
            <nav className="space-y-1">
              {sidebarItems.map((item) => (
                item.children ? (
                  <div key={item.name}>
                    <button
                      onClick={() => setMastersOpen(!mastersOpen)}
                      className={`sidebar-item w-full justify-between ${location.pathname.includes("/admin/masters") ? 'text-[#0A4CAE]' : ''}`}
                    >
                      <div className="flex items-center gap-3">
                        <item.icon size={20} />
                        <span>{item.name}</span>
                      </div>
                      <ChevronDown 
                        size={16} 
                        className={`transition-transform ${mastersOpen ? 'rotate-180' : ''}`} 
                      />
                    </button>
                    {mastersOpen && (
                      <div className="ml-4 mt-1 space-y-1 border-l border-gray-200 pl-3">
                        {item.children.map((child) => (
                          <NavLink
                            key={child.path}
                            to={child.path}
                            onClick={() => setMobileMenuOpen(false)}
                            className={({ isActive }) => 
                              `sidebar-item text-sm ${isActive ? 'active' : ''}`
                            }
                          >
                            <child.icon size={18} />
                            <span>{child.name}</span>
                          </NavLink>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={({ isActive }) => 
                      `sidebar-item ${isActive ? 'active' : ''}`
                    }
                  >
                    <item.icon size={20} />
                    <span>{item.name}</span>
                  </NavLink>
                )
              ))}
            </nav>
            <div className="mt-4 pt-4 border-t border-gray-200">
              <Button
                onClick={handleLogout}
                variant="ghost"
                className="w-full justify-start gap-3 text-gray-600 hover:text-gray-900 hover:bg-gray-100"
              >
                <LogOut size={20} />
                <span>Logout</span>
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="h-screen min-w-0 flex-1 overflow-y-auto pt-16 md:ml-0 md:pt-0">
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>

      <Dialog open={Boolean(newOrder)} onOpenChange={(open) => !open && closeNewOrderModal()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="mx-auto sm:mx-0 mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-blue-50 text-[#0A4CAE]">
              <BellRing size={24} />
            </div>
            <DialogTitle>
              {newOrderCount > 1 ? `${newOrderCount} new orders received` : "New order received"}
            </DialogTitle>
            <DialogDescription>
              A new order has been placed and is ready for review.
            </DialogDescription>
          </DialogHeader>

          {newOrder && (
            <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm">
              <div className="flex items-center justify-between gap-4">
                <span className="text-gray-500">Order</span>
                <span className="font-semibold text-gray-900">#{newOrder.id}</span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-4">
                <span className="text-gray-500">Customer</span>
                <span className="font-medium text-gray-900">{newOrder.user_name || newOrder.user_phone}</span>
              </div>
              <div className="mt-2 flex items-center justify-between gap-4">
                <span className="text-gray-500">Amount</span>
                <span className="font-semibold text-gray-900">
                  ₹{Number(newOrder.total_amount || 0).toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={closeNewOrderModal}>
              Dismiss
            </Button>
            <Button
              className="bg-[#0A4CAE] hover:bg-[#083d8c]"
              onClick={() => {
                const orderId = newOrder?.id;
                closeNewOrderModal();
                if (orderId) navigate(`/admin/orders/${orderId}`);
              }}
            >
              View Order
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLayout;
